import { memo, useState, useMemo } from 'react';
import classNames from 'classnames';
import { IconButton } from '@carbon/react';
import {
  ChevronDown,
  ChevronUp,
  DocumentView,
  ViewOff,
} from '@carbon/react/icons';
import { InterpretationDrawer } from 'components/interpretation-view/interpretation-view';
import type { DocumentRow, CardInterpretation } from '../types';
import { PartySummaryBlock } from './party-summary-block';
import { EntityAsFoundColumn } from './entity-as-found-column';
import { DocumentCardTitle } from './document-card-title';
import { DocumentCardDates } from './document-card-dates';
import { DocumentCardInterpretations } from './document-card-interpretations';
import { DocumentCardAreas } from './document-card-areas';
import { DocumentCardExpand } from './document-card-expand';
import { useCardOpenState } from './hooks';
import { occurrenceKeys } from 'utils/occurrence-keys';
import { isTypedExpandType, type ExpandAllBroadcast } from './utils';
import styles from './document-card.module.scss';

interface DocumentCardProps {
  document: DocumentRow;
  selected: boolean;
  // Takes the document so the caller can pass one stable function (rather
  // than a fresh per-row closure) and still know which card fired it — that
  // stability is what lets this component be wrapped in memo() below.
  onSelect: (document: DocumentRow) => void;
  // Left-accent color from the active "Color by Attribute" rule (mirrors the map).
  accentColor?: string;
  // Broadcast from the "expand/collapse all" toolbar control. A new object on
  // each click re-syncs this card's expanded state; individual toggling still
  // works afterward.
  expandAll?: ExpandAllBroadcast;
  // The prev/next-navigated card: draws the blue active-accent line.
  active?: boolean;
  // Open pulse for the navigated card — a fresh object on each prev/next click
  // opens this card and all its sections (scoped where expandAll is global).
  navOpen?: { open: boolean };
  // Toggle the side-by-side document image preview (mirrors the /documents page).
  // Takes the document id for the same reason onSelect takes the document.
  onPreview?: (documentId: number) => void;
  previewing?: boolean;
  // Builds the document page link, tagging where the reader came from so the
  // document's breadcrumb leads back there.
  documentLink: (href: string) => string;
}

/**
 * The card's values derived from its document: the summarized (latest)
 * interpretation, the Applicable Areas column, the note and whether the card
 * has anything to expand.
 * @param {DocumentRow} document The document.
 * @return {object} The derived values.
 */
function useCardContent(document: DocumentRow) {
  const interpretations = useMemo(
    () => document.interpretations ?? [],
    [document.interpretations]
  );
  const count = document.interpretationsCount ?? interpretations.length;
  // Interpretations arrive oldest-first (sequence asc); the card summarizes the
  // latest one, and lists them latest-first to mirror Global Interpretations.
  const latestFirst = useMemo(
    () => [...interpretations].reverse(),
    [interpretations]
  );
  const primaryInterp = latestFirst.at(0);
  // Fallback for documents with no interpretation-scoped applicable areas: the
  // document's own reference location(s), which are what geolocate it on the map.
  const referenceAreas = useMemo(
    () =>
      (document.referenceLocations ?? [])
        .map((rl) => rl.shortName || rl.name)
        .filter((name): name is string => !!name),
    [document.referenceLocations]
  );
  const areas = primaryInterp?.applicableAreas ?? [];
  const collapsedAreas =
    areas.length > 0 ? areas.map((area) => area.label) : referenceAreas;
  // Notes come from two valid sources: the interpretation's note and the
  // document's Related Items notes (kind: 'note'). Show all of them.
  const note =
    [primaryInterp?.note, ...(document.relatedNotes ?? [])]
      .filter(Boolean)
      .join('\n\n') || undefined;
  const isTypedExpand = isTypedExpandType(document.type);
  const hasExpand =
    isTypedExpand ||
    count > 0 ||
    collapsedAreas.length > 0 ||
    !!note ||
    (document.workspaces ?? []).length > 0 ||
    !!document.partiesBreakdown;
  return {
    interpretations,
    count,
    latestFirst,
    primaryInterp,
    collapsedAreas,
    note,
    isTypedExpand,
    hasExpand,
  };
}

/**
 * One document of the runsheet: its summary columns and, once expanded, its
 * parties, areas, transactions, curative requirements, notes and workspaces.
 * @param {DocumentCardProps} props The card.
 * @return {JSX.Element} The card.
 */
function DocumentCardImpl({
  document,
  selected,
  onSelect,
  accentColor,
  expandAll,
  active,
  navOpen,
  onPreview,
  previewing,
  documentLink,
}: DocumentCardProps) {
  // Preview is offered for any document with a numeric id (matches the
  // /documents page); some rows carry a non-numeric or missing id.
  const documentId = Number(document.id);
  const canPreview = Number.isFinite(documentId);
  const {
    expanded,
    everExpanded,
    sectionSignal,
    partiesSignal,
    toggleExpand,
    openParties,
  } = useCardOpenState(expandAll, navOpen);
  // Which interpretation's tear sheet is open (one shared drawer per card).
  const [openInterp, setOpenInterp] = useState<CardInterpretation | null>(null);
  const content = useCardContent(document);
  const { hasExpand } = content;
  const partySources = document.partySources ?? [];
  const partySourceKeys = occurrenceKeys(
    partySources.map((source) => source.source ?? source.sourceLabel ?? '')
  );

  /** Toggles the card (when it has a body) and selects it. */
  const activate = () => {
    if (hasExpand) toggleExpand();
    onSelect(document);
  };

  return (
    <div
      className={classNames(selected ? styles.cardSelected : styles.card, {
        [styles.cardPreviewing]: previewing,
        [styles.cardActive]: active,
        [styles.cardExpanded]: expanded && hasExpand,
        [styles.cardSevenColumns]: !!document.entityAsFound,
      })}
      role="button"
      tabIndex={0}
      onClick={activate}
      onKeyDown={(e) => {
        // Only handle keys aimed at the card itself — let Enter/Space reach
        // inner links and buttons instead of hijacking their activation.
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          activate();
        }
      }}
    >
      {accentColor && (
        <span
          className={styles.accentBar}
          style={{ backgroundColor: accentColor }}
        />
      )}
      <div className={styles.summary}>
        <div className={styles.chevronCol}>
          {hasExpand && (
            <IconButton
              kind="ghost"
              size="sm"
              align="bottom"
              autoAlign
              label={expanded ? 'Collapse' : 'Expand'}
              aria-expanded={expanded}
              onClick={(e) => {
                e.stopPropagation();
                toggleExpand();
                onSelect(document);
              }}
            >
              {expanded ? <ChevronUp /> : <ChevronDown />}
            </IconButton>
          )}
          {onPreview && canPreview && (
            <IconButton
              kind="ghost"
              size="sm"
              align="bottom"
              autoAlign
              label={previewing ? 'Close preview' : 'Preview document'}
              aria-pressed={previewing}
              onClick={(e) => {
                e.stopPropagation();
                onPreview(documentId);
              }}
            >
              {previewing ? <ViewOff /> : <DocumentView />}
            </IconButton>
          )}
          {/* Open-document launch icon removed from the leading cluster to
              match the mock (chevron + preview only); the doc opens via the
              citation/title links. */}
        </div>
        <DocumentCardTitle
          document={document}
          documentLink={documentLink}
          hasExpand={hasExpand}
        />

        <DocumentCardDates
          document={document}
          primaryInterp={content.primaryInterp}
        />

        {document.partySummary && (
          <div className={styles.parties}>
            <PartySummaryBlock
              summary={document.partySummary}
              onShowMore={openParties}
            />
          </div>
        )}

        {/* Typed docs (AFE/DO/RC): the unified multi-source Source column — one
            "Source: X" block per source (Autoprovision, Salesforce), stacked,
            matching the non-typed column (mock parity). */}
        {partySources.length > 0 && (
          <div className={styles.parties}>
            {partySources.map((source, i) => (
              <PartySummaryBlock
                key={partySourceKeys[i]}
                summary={source}
                onShowMore={openParties}
              />
            ))}
          </div>
        )}

        {document.entityAsFound && (
          <EntityAsFoundColumn appearances={document.entityAsFound} />
        )}

        <DocumentCardInterpretations
          interpretations={content.latestFirst}
          count={content.count}
          onOpen={setOpenInterp}
        />

        <DocumentCardAreas areas={content.collapsedAreas} />
      </div>

      {hasExpand && (
        <div
          className={classNames(styles.expandReveal, {
            [styles.expandRevealOpen]: expanded,
          })}
        >
          {/* Mounted on first open, then kept: typed expands fetch on mount,
              and keeping it lets the height reveal animate the collapse too. */}
          {everExpanded && (
            <div
              className={classNames(styles.expanded, {
                [styles.expandedAccordions]: !content.isTypedExpand,
              })}
              role="presentation"
              // Clicking the expanded body flies to the map dot like the header,
              // but stops here so it doesn't bubble to the card's collapse toggle.
              // Inner buttons/links stop propagation of their own.
              onClick={(e) => {
                e.stopPropagation();
                onSelect(document);
              }}
            >
              <DocumentCardExpand
                document={document}
                areas={content.collapsedAreas}
                note={content.note}
                sectionSignal={sectionSignal}
                partiesSignal={partiesSignal}
                interpretations={content.interpretations}
                count={content.count}
                onOpenInterp={setOpenInterp}
                isTypedExpand={content.isTypedExpand}
              />
            </div>
          )}
        </div>
      )}

      {/* One interpretation tear sheet per card; it self-fetches from the href.
          Clicks are stopped so interacting with the drawer never toggles the
          card behind it. */}
      <div
        role="presentation"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <InterpretationDrawer
          open={!!openInterp}
          onClose={() => setOpenInterp(null)}
          documentTitle={document.title ?? ''}
          interpretationHref={openInterp?.href ?? ''}
          // The document card is document-centric, so open the tear sheet
          // UNSCOPED — same as Global Interpretations. Passing a title workspace
          // spatially scopes the interpretation to that one workspace's survey
          // geometry, which collapsed 200+ applicable areas down to 1.
          documentId={documentId}
          showEditButton={false}
        />
      </div>
    </div>
  );
}

export const DocumentCard = memo(DocumentCardImpl);
